/**
 * Shared-state payload contract — matches what the
 * `ivthub-motion-wave-select` skill returns under the `state_snapshot`
 * sentinel key, plumbed through Gateway → ChatPanel → agentStateStore
 * under namespace `ivt.motion.wave`.
 *
 * Type names align with the SPEC (IVTHubSpec
 * tech/motion/asset-account-matching-logic.md §七 output shape).
 */

export interface CandidateOpp {
  opp_id: string;
  opp_name: string;
  arr: number;
  stage: string;
  owner: string;
  /** L1 opp-level match reasons — heuristic derived from stage/ARR/owner
   *  (see skill `_derive_opp_reasons_action`). Same shape as account-level
   *  CandidateReason so both can be rendered by the same cell component.
   *  Optional to keep back-compat with any older cached snapshot in the store. */
  reasons?: CandidateReason[];
  /** L1 opp-level suggested next step, keyed on stage bucket. Falls back
   *  to a generic prompt when the stage is unrecognised. */
  action?: string;
  /** IVT tags attached to this opportunity (GCR-IVT-* namespace, see
   *  IVTHubSpec tech/motion/asset-account-matching-logic.md §3.8 SQL⑦).
   *  Empty when the opp has no tag yet — the panel renders it as no chips. */
  tags?: string[];
  /** 1-5 relevance from ListRelevantPipelines (DAT-8634). Lives on the OPP and
   *  not on the account because that is the grain the judgment has: the offline
   *  judge scores (opportunity x asset) pairs, collapsed to the best asset per
   *  opportunity. There is no account-level relevance, so the account row leaves
   *  the column empty rather than showing a roll-up that does not exist upstream.
   *  Absent on the Rocky-snapshot path, which carries its own account score. */
  relevance_score?: number;
}

export interface CandidateFlag {
  icon: string;
  tip: string;
}

export interface CandidateReason {
  type: string;
  text: string;
}

export interface CandidateAccount {
  account_id: string;
  account_name: string;
  industry: string;
  score: number;
  flags: CandidateFlag[];
  open_opps: CandidateOpp[];
  reasons: CandidateReason[];
  action: string;
  /** SFDC account owner alias (dim_sfdc_accounts.sfdc_account_owner_alias).
   *  Optional — legacy snapshots cached before the skill exposed this field
   *  do not carry it, and Greenfield accounts with no assigned owner in
   *  Redshift also lack one. Rendered as-is on the Account row's Owner column. */
  owner?: string;
}

/**
 * Query the panel should RUN, as opposed to results it should render.
 *
 * The AGUI side-channel has a hard size limit, so the skill sends the query it
 * decided on and the panel fetches the rows itself — that is the whole reason
 * this shape exists rather than the skill shipping candidates. See
 * IVTHubSpec tech/motion/agui-wave-select.md.
 *
 * Defined in relevanceQuery.ts as the generated request type minus the fields
 * only the panel can supply, so the Smithy model stays the single authority and
 * a renamed input is a compile error rather than a field the skill can emit and
 * the panel silently drops.
 */
import type { RelevanceQuerySpec } from './relevanceQuery';
export type { RelevanceQuerySpec };

export interface WaveSnapshot {
  asset_name?: string;
  /** Present when Rocky is telling the panel WHAT TO QUERY rather than handing
   *  it candidates. Independent of `results`: a snapshot may carry either, and
   *  `results` wins when both are present because an explicit candidate set is a
   *  narrower statement than a query. */
  query_spec?: RelevanceQuerySpec;
  match_count: number;
  has_pipeline_count?: number;
  no_pipeline_count?: number;
  results: CandidateAccount[];
  /**
   * How this candidate set was produced. Optional — a real Wave is often
   * a composite of multiple intents (recommend + refine + add_by_ids),
   * so the field stays untyped pending the SPEC decision on whether to
   * record a single enum or an origins audit list. See DAT-8312 follow-up.
   */
  method?: 'PROFILE_MATCH' | 'TAG_IMPORT' | 'MANUAL';
}

// ---------------------------------------------------------------------------
// API payload shapes — mirror IVTHubModel motion.smithy `Claim` /
// `ClaimConflict` / `ClaimAccountsOutput`. Defined locally so the panel can
// build / type-check the Confirm payload before the regenerated
// IVTHubTSClient is consumable. DAT-8312 follow-up will replace these
// hand-rolled types with imports from `@amzn/ivthub-client`.
// ---------------------------------------------------------------------------

/** Account-level claim → the whole account joins the wave. */
export type PoolType = 'EXPLORE' | 'ACCELERATE';

export interface Claim {
  accountName: string;
  accountId?: string;
  opptyId?: string;
  oppName?: string;
  arr?: number;
  oppStage?: string;
  poolType: PoolType;
  /** Wave-select rationale — flattened from CandidateReason[] into one line
   *  ("keyword X · big-bet · in-touch"). Persisted on motion_opps.match_reason
   *  / motion_accounts.match_reason and echoed by GetMotionOverview. */
  matchReason?: string;
  /** Suggested next best action from the skill (e.g. "已在接触，安排 Demo"). */
  suggestedAction?: string;
}

export interface ClaimConflict {
  accountName: string;
  opptyId?: string;
  reason: string;
}

export interface ClaimAccountsResult {
  claimed: number;
  skipped: number;
  conflicts: ClaimConflict[];
}

/** Cloudscape Table works with a generic row type; rows are CandidateAccount. */
export type WaveRow = CandidateAccount;

// ---------------------------------------------------------------------------
// Table row union — Cloudscape's expandableRows API requires parents AND
// children to share a single row type. We tag account vs. opp rows with a
// `kind` discriminator so column cells can branch on row identity, while
// keeping selection/sorting type-safe.
// ---------------------------------------------------------------------------

export type AccountTableRow = CandidateAccount & { kind: 'account' };

export type OppTableRow = CandidateOpp & {
  kind: 'opp';
  /** Parent account — used for sort comparators on opp rows so children
   *  stay co-located with their parent during sorting on account fields. */
  parent_account_id: string;
  parent_account_name: string;
};

export type TableRow = AccountTableRow | OppTableRow;

export const isAccountRow = (r: TableRow): r is AccountTableRow => r.kind === 'account';
export const isOppRow = (r: TableRow): r is OppTableRow => r.kind === 'opp';
